/**
 * Slash-mention autocomplete support for the chatbot input.
 *
 * Skills are sent to the backend as ordinary prose — typing `/<name>` does
 * not invoke server-side directly. The model sees the verbatim message
 * (with `AVAILABLE SKILLS` already in its system prompt) and decides
 * whether to call ``invoke_skill``. The frontend's only job is autocomplete:
 * find the slash-token the cursor is currently inside and suggest matches.
 */

export interface SlashTokenAtCursor {
  /** Index of the leading `/` in `value`. */
  start: number;
  /** Exclusive end index (== cursor when typing forward). */
  end: number;
  /** Substring between `/` and `end`, excluding the slash itself. */
  query: string;
}

/**
 * Find the slash-token the cursor is currently sitting at, anywhere in the
 * input. Returns null when the cursor is not inside an active slash mention.
 *
 * A slash token is `/<\S*>` immediately preceded by start-of-text or whitespace,
 * with the cursor positioned at its end (no characters between cursor and the
 * last non-whitespace char). This means:
 *
 *   - "/" at index 0, cursor=1 → active (empty query)
 *   - "hi /tri", cursor=7 → active (query="tri")
 *   - "/tri hi", cursor=7 → null (cursor is after the token, on whitespace)
 *   - "hello", cursor=5 → null (no slash)
 *   - "a/b", cursor=3 → null (slash not preceded by whitespace; not a mention)
 *
 * Inserting a skill name into the input replaces ``[start, end)`` with the
 * chosen name + trailing space.
 */
export function findSlashTokenAtCursor(
  value: string,
  cursor: number,
): SlashTokenAtCursor | null {
  if (cursor < 1 || cursor > value.length) return null;
  // Walk backward from the cursor until we hit whitespace or a slash.
  let i = cursor - 1;
  while (i >= 0 && !/\s/.test(value[i]) && value[i] !== "/") {
    i--;
  }
  if (i < 0 || value[i] !== "/") return null;
  // Slash must be at start-of-text or preceded by whitespace.
  if (i > 0 && !/\s/.test(value[i - 1])) return null;
  return {
    start: i,
    end: cursor,
    query: value.slice(i + 1, cursor),
  };
}
